<template>
    <div class="container">
        <el-breadcrumb separator="/">
            <el-breadcrumb-item :to="{ name: 'Organisation' }"
                >Dashboard</el-breadcrumb-item
            >
            <el-breadcrumb-item>Users</el-breadcrumb-item>
        </el-breadcrumb>

        <h1 class="container__title h1">Users</h1>

        <div class="container__section container__section--space-small">
            <Button @click="dialogVisible = true" :showIcon="true">
                Add user
                <IconPlus slot="icon" />
            </Button>
        </div>

        <!-- users -->
        <div
            class="container__section container__section--space-small"
            v-if="users && users.length > 0"
        >
            <div class="users">
                <div class="users__inner">
                    <div class="users__row users__row--head">
                        <div class="users__col">Name</div>
                        <div class="users__col">E-mail</div>
                        <!-- <div class="users__col">Role</div> -->
                    </div>
                    <div
                        class="users__row"
                        v-for="(user, index) in users"
                        :key="index"
                    >
                        <div class="users__col">
                            {{ user.first_name }} {{ user.last_name }}
                        </div>
                        <div class="users__col">{{ user.email }}</div>
                        <!-- <div class="users__col">&nbsp;</div> -->
                        <div class="users__col">
                            <div
                                class="users__action"
                                @click="deleteUser(user)"
                            >
                                Delete
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- invited users -->
        <div
            class="container__section container__section--space-small"
            v-if="invitations && invitations.length > 0"
        >
            <div class="users users--invitations">
                <div class="users__inner">
                    <div class="users__row users__row--head">
                        <div class="users__col">Name</div>
                        <div class="users__col">E-mail</div>
                        <!-- <div class="users__col">Role</div> -->
                    </div>
                    <div
                        class="users__row"
                        v-for="(invitation, index) in invitations"
                        :key="index"
                    >
                        <div class="users__col"></div>
                        <div class="users__col">{{ invitation.email }}</div>
                        <!-- <div class="users__col">
                            {{ invitation.role.data.name }}
                        </div> -->
                        <div class="users__col">
                            <div
                                class="users__action"
                                @click="deleteInvitation(invitation)"
                            >
                                Delete
                            </div>
                        </div>
                        <div class="users__col">
                            <div
                                class="users__action"
                                @click="resendInvitation(invitation)"
                            >
                                Resend
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog
            :visible.sync="dialogVisible"
            :before-close="clearFormdata"
            :destroy-on-close="true"
        >
            <h2 class="container__title h2">Invite a new user</h2>
            <el-form ref="form" :model="formData" :rules="validationRules">
                <el-form-item
                    label="E-Mail"
                    class="space-bottom--medium"
                    prop="short_name"
                >
                    <el-input
                        v-model="formData.email"
                        type="email"
                        @input="touchForm"
                    ></el-input>
                </el-form-item>
                <el-form-item>
                    <Button size="big" @click="submitForm">Invite</Button>
                </el-form-item>
            </el-form>
        </el-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as api from '@/api';
import Button from '@/components/Button.vue';
import IconPlus from '@/components/icons/IconPlus.vue';

export default {
    components: {
        Button,
        IconPlus,
    },
    data() {
        return {
            formTouched: false,
            users: false,
            invitations: false,
            dialogVisible: false,
            formData: {
                email: '',
                role_id: 1,
            },
            validationRules: {
                email: [
                    {
                        required: true,
                        message: 'Please enter an e-mail address',
                        trigger: 'blur',
                    },
                    {
                        type: 'email',
                        message: 'Please input a valid email address.',
                        trigger: ['blur', 'change'],
                    },
                ],
            },
        };
    },
    computed: {
        ...mapGetters(['organisation']),
    },
    watch: {
        organisation(newVal) {
            if (newVal.slug) {
                this.loadUsers();
                this.loadInvites();
            }
        },
    },
    mounted() {
        if (this.organisation) {
            this.loadUsers();
            this.loadInvites();
        }
    },
    methods: {
        async loadUsers() {
            try {
                const response = await api.organisation.users(
                    this.organisation.slug,
                );
                this.users = response.data.data;
            } catch (error) {
                console.error(error);
            }
        },
        async loadInvites() {
            try {
                const response = await api.organisation.pendingInvites(
                    this.organisation.slug,
                );
                this.invitations = response.data.data;
            } catch (error) {
                console.error(error);
            }
        },
        touchForm() {
            this.formTouched = true;
        },
        submitForm() {
            this.$refs.form.validate(valid => {
                if (valid) {
                    this.formTouched = false;
                    this.sendForm();
                } else {
                    return false;
                }
            });
        },
        clearFormdata() {
            this.formData.email = '';
            this.dialogVisible = false;
        },
        async sendForm() {
            try {
                const response = await api.organisation.invite(
                    this.organisation.slug,
                    this.formData,
                );
                if (response) {
                    this.$message({
                        message: 'Invitation successfully sent!',
                        type: 'success',
                    });

                    this.clearFormdata();
                    this.dialogVisible = false;
                    this.loadInvites();
                }
            } catch (error) {
                console.error(error);
            }
        },
        deleteInvitation(invitation) {
            this.$confirm(
                'Are you sure you want to delete this invitation?',
                'Warning',
                {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    type: 'warning',
                },
            )
                .then(() => {
                    this.sendInvitationDeletion(invitation);
                })
                .catch(() => {
                    console.log('cancelled');
                });
        },
        async sendInvitationDeletion(invitation) {
            try {
                const response = await api.organisation.deleteInvitation(
                    this.organisation.slug,
                    invitation.id,
                );
                this.loadInvites();
                this.$message({
                    message: 'Invitation deleted succesfully!',
                    type: 'success',
                });
            } catch (error) {
                console.error(error);
            }
        },
        resendInvitation(invitation) {
            console.log('there is no api endpoint for this yet');
        },
        deleteUser(user) {
            this.$confirm(
                `Are you sure you want to delete the user ${user.first_name} ${user.last_name}?`,
                'Warning',
                {
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    type: 'warning',
                },
            )
                .then(() => {
                    this.sendUserDeletion(user);
                })
                .catch(() => {
                    console.log('cancelled');
                });
        },
        async sendUserDeletion(user) {
            try {
                const response = await api.organisation.deleteUser(
                    this.organisation.slug,
                    user.id,
                );
                this.loadUsers();
                this.$message({
                    message: 'User deleted succesfully!',
                    type: 'success',
                });
            } catch (error) {
                console.error(error);
            }
        },
    },
};
</script>

<style lang="scss">
.users {
    font-weight: $font-weight-regular;
    font-size: 14px;

    @include mq($until: tablet-landscape) {
        font-size: 12px;
    }

    @include mq($until: tablet) {
        overflow: auto;
        padding-bottom: 10px;
    }

    &--invitations {
        color: $medium-light-grey;
    }

    &__inner {
        @include mq($until: tablet) {
            min-width: 700px;
        }
    }

    &__row {
        display: grid;
        grid-template-columns: 2fr 3fr 2fr 1fr 1fr;
        padding: 10px 0;

        @include mq($until: tablet-landscape) {
            padding: 5px 0;
        }

        &--head {
            border-bottom: 1px solid $light-grey;
            text-transform: uppercase;
            font-size: 12px;
            font-weight: $font-weight-bold;
            letter-spacing: 0.15em;
            padding-bottom: 20px;
            margin-bottom: 10px;

            @include mq($until: tablet-landscape) {
                font-size: 10px;
                padding-bottom: 10px;
                margin-bottom: 5px;
            }
        }
    }

    &__action {
        font-weight: $font-weight-bold;
        text-transform: uppercase;
        letter-spacing: 0.15em;
        font-size: 12px;
        color: $medium-grey;
        transition: $link-transition;
        cursor: pointer;

        @include mq($until: tablet-landscape) {
            font-size: 10px;
        }

        .no-touch &:hover {
            color: $almost-black;
        }
    }
}
</style>
